import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMobileDetect } from '../hooks/useMobileDetect';

interface MobileRedirectProps {
  children: React.ReactNode;
}

/**
 * Route mapping from desktop to mobile paths
 */
const DESKTOP_TO_MOBILE_ROUTES: Record<string, string> = {
  '/': '/m',
  '/formspace': '/m/forms',
  '/formspace/': '/m/forms',
};

/**
 * Pattern matching for dynamic routes
 */
const DYNAMIC_ROUTE_PATTERNS: Array<{
  desktop: RegExp;
  mobilePath: (match: RegExpMatchArray) => string;
}> = [
  {
    // /formspace/builder/:id -> /m/builder/:id
    desktop: /^\/formspace\/builder\/([^/]+)$/,
    mobilePath: (match) => `/m/builder/${match[1]}`,
  },
  {
    // /formspace/live-quiz/:id -> /m/live-quiz/:id
    desktop: /^\/formspace\/live-quiz\/([^/]+)$/,
    mobilePath: (match) => `/m/live-quiz/${match[1]}`,
  },
];

/**
 * Component that automatically redirects mobile users to mobile-optimized routes
 */
export default function MobileRedirect({ children }: MobileRedirectProps) {
  const { isMobile } = useMobileDetect();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isMobile) return;

    const currentPath = location.pathname;

    // Skip if already on mobile route
    if (currentPath.startsWith('/m/') || currentPath.startsWith('/m')) {
      return;
    }

    // Check static routes
    const staticMobilePath = DESKTOP_TO_MOBILE_ROUTES[currentPath];
    if (staticMobilePath) {
      navigate(staticMobilePath, { replace: true });
      return;
    }

    // Check dynamic route patterns
    for (const { desktop, mobilePath } of DYNAMIC_ROUTE_PATTERNS) {
      const match = currentPath.match(desktop);
      if (match) {
        navigate(mobilePath(match), { replace: true });
        return;
      }
    }
  }, [isMobile, location.pathname, navigate]);

  return <>{children}</>;
}
